import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { session } from '../../core/session.js';
import { reload } from '../../app/shell.js';
import { av, bar, chips, ic, tag } from '../../ui/components.js';
import { field, radioField, tagField } from '../../ui/forms.js';
import { confirmBox } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { loadResume, resumeCard } from '../resume/card.js';
import { cofounderCard, loadCofounder } from '../network/cofounder.js';

const profile = {
  path: '/student/profile',
  roles: ['student'],
  title: 'Profile',
  async load(ctx) {
    const [p, projects, cofounder, resume] = await Promise.all([
      api.get('/student/profile', { signal: ctx.signal }),
      api.get('/student/projects', { signal: ctx.signal }),
      loadCofounder(ctx.signal),
      loadResume(ctx.signal),
    ]);
    return { ...p, projects: projects.projects, cofounder, resume };
  },
  view(d) {
    const p = d.profile;
    const name = session.user.name;
    return html`<div class="card"><div class="row nw">${av(name, 'lg')}<div class="f1"><h2 style="margin:0">${name}</h2>
      <p class="mu" style="margin:0">${[p.degree, p.college].filter(Boolean).join(', ') || (p.seeker_type === 'job_seeker' ? 'Add your skills and experience below' : 'Add your degree and college below')}${p.goal ? `. Goal: ${p.goal}` : ''}</p>${p.seeker_type === 'job_seeker' ? html`<div class="mt-s">${tag('Job seeker')}${p.last_job_title ? html` <span class="sm mu">Last role: ${p.last_job_title}</span>` : ''}</div>` : ''}</div></div>
      <div class="mt-s">${chips(p.skills)}</div></div>
      <div class="grid g2 mt" style="align-items:start">
        <div class="card"><div class="row between"><h3 style="margin:0">Profile strength</h3><b>${d.strength.percent}%</b></div>${bar(d.strength.percent, 'ok', 'Profile strength')}
          <div class="list mt-s sm">${d.strength.checks.map((c) => html`<div class="li">${c.done ? ic('check', 'check') : ic('x', 'cross')}<span class="f1">${c.name}</span>
            ${!c.done && c.name === 'Certificate' ? html`<a class="btn soft sm" href="/student/progress?tab=assessments">Take test</a>` : ''}</div>`)}</div></div>
        <div class="card"><h3>Edit profile</h3>
          <form data-submit="sprofile.save" novalidate>
            ${radioField('seeker_type', 'I am', [['Student', 'Studying or just finished'], ['Job seeker', 'Fresher or experienced, looking for a job']], p.seeker_type === 'job_seeker' ? 'Job seeker' : 'Student')}
            ${field('pf-job', 'Last job title', { name: 'last_job_title', req: false, val: p.last_job_title, maxlength: 120, help: 'Only if you have worked before.' })}
            ${tagField('pf-skills', 'Skills you have', { name: 'skills', preset: p.skills, req: false })}
            ${field('pf-learn', 'What you want to learn', { name: 'learn_goal', req: false, val: p.learn_goal, max: 200 })}
            ${field('pf-goal', 'Career goal', { name: 'goal', req: false, val: p.goal, maxlength: 160 })}
            <div class="fgrid">${field('pf-col', 'College', { name: 'college', req: false, val: p.college, maxlength: 160 })}${field('pf-deg', 'Degree and branch', { name: 'degree', req: false, val: p.degree, maxlength: 160 })}</div>
            <div class="fgrid">${field('pf-city', 'City', { name: 'city', req: false, val: p.city, maxlength: 80 })}${field('pf-exp', 'Years of work experience', { type: 'number', name: 'experience_years', min: 0, max: 60, req: false, val: p.experience_years ?? '', help: 'Enter 0 if you are a fresher. Companies match on this.' })}${field('pf-link', 'Portfolio or GitHub', { type: 'url', name: 'portfolio_url', req: false, val: p.portfolio_url, ph: 'https://github.com/you' })}</div>
            <button class="btn" type="submit">Save profile</button></form></div></div>
      <div class="mt">${resumeCard(d.resume)}</div>
      <div class="grid g2 mt" style="align-items:start">
        <div class="card"><h3>Projects</h3>
          ${d.projects.length ? html`<div class="list">${d.projects.map((x) => html`<div class="li"><span class="f1"><b>${x.title}</b>${x.verified ? html` <span class="tag ok">Verified</span>` : ''}<br><span class="sm mu">${x.description}</span>
            ${x.link ? html`<br><a class="sm" href="${x.link}" target="_blank" rel="noopener noreferrer">${x.link}</a>` : ''}</span><button type="button" class="btn ghost sm" data-act="project.delete" data-id="${x.id}" aria-label="Remove ${x.title}">${ic('x')}</button></div>`)}</div>` : html`<p class="mu sm">Projects you list here help companies see your work.</p>`}
          <form data-submit="project.add" class="mt-s" novalidate>${field('pj-title', 'Project name', { name: 'title', minlen: 2, maxlength: 160 })}
            ${field('pj-desc', 'What did you build?', { name: 'description', rows: 2, req: false, max: 3000 })}${field('pj-link', 'Link', { type: 'url', name: 'link', req: false, ph: 'https://' })}
            <button class="btn ghost" type="submit">${ic('plus')} Add project</button></form></div>
        ${cofounderCard(d.cofounder)}</div>`;
  },
};

register({
  'sprofile.save': (form) => submitForm(form, async (values) => {
    const body = { seeker_type: values.seeker_type === 'Job seeker' ? 'job_seeker' : 'student', last_job_title: values.last_job_title || '', skills: values.skills, learn_goal: values.learn_goal, goal: values.goal, college: values.college, degree: values.degree, city: values.city };
    if (values.portfolio_url) body.portfolio_url = values.portfolio_url;
    if (values.experience_years !== '' && values.experience_years != null) body.experience_years = Number(values.experience_years);
    await api.put('/student/profile', { body });
    toast('Profile saved. Your matches were updated.');
    await reload();
  }),
  'project.add': (form) => submitForm(form, async (values) => {
    await api.post('/student/projects', { body: values });
    form.reset();
    toast('Project added');
    await reload();
  }),
  'project.delete': async (el) => {
    if (!(await confirmBox({ title: 'Remove this project?', text: 'It will no longer show on your profile.', label: 'Remove', danger: true }))) return;
    await api.delete(`/student/projects/${el.dataset.id}`);
    await reload();
  },
});

export const routes = [profile];
